// ManageEventRegistration.js

import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEventContext } from '../../context/EventContext';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme';
import Button from '../Button/Button';
import Typography from '../Typography';
import Modal from '../Modal/Modal';
import AddTicketForm from '../Forms/AddTicketForm';
import EditTicketForm from '../Forms/EditTicketForm';
import Tag from '../Tag';
import TicketCard from './TicketCard';
import AddIcon from '../Icons/AddIcon';
import { isEventPast } from '../../utils/dateUtils';
import { TICKET_PAID } from '../../constants/eventTypes';

const ManageEventRegistration = () => {
  const { t } = useTranslation('manageEventRegistration');
  const { id } = useParams();
  const { eventDetails, updateEventDetails, updateEventCapacity } = useEventContext();
  const { user, stripeStatus, refreshStripeStatus } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [ticketToEdit, setTicketToEdit] = useState(null);

  // Cargar los tickets existentes del evento
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axiosClient.get(`/api/tickets/event/${id}`);
        if (response.data.success) {
          setTickets(response.data.tickets);
        } else {
          toast.error(response.data.message || 'Error al obtener los tickets.');
        }
      } catch (error) {
        console.error('Error fetching tickets:', error);
        toast.error('Error al obtener los tickets.');
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [id]);

  // Funciones para manejar los modales
  const openAddModal = () => {
    if (isEventPast(eventDetails.endDate) || eventDetails?.status === 'cancelled') {
      if (eventDetails?.status === 'cancelled') {
        toast.error('No puedes agregar tickets a un evento cancelado.');
      } else {
        toast.error('No puedes agregar tickets a un evento que ya ha finalizado.');
      }
      return;
    }
    setIsAddModalOpen(true);
  };
  const closeAddModal = () => setIsAddModalOpen(false);
  const openEditModal = (ticket) => {
    if (isEventPast(eventDetails.endDate) || eventDetails?.status === 'cancelled') {
      if (eventDetails?.status === 'cancelled') {
        toast.error('No puedes editar tickets de un evento cancelado.');
      } else {
        toast.error('No puedes editar tickets de un evento que ya ha finalizado.');
      }
      return;
    }
    setTicketToEdit(ticket);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setTicketToEdit(null);
    setIsEditModalOpen(false);
  };

  // Función auxiliar para actualizar la capacidad total del evento
  const updateEventCapacityFromAPI = async () => {
    try {
      const response = await axiosClient.get(`/api/events/manage/${id}`);
      if (response.data.success) {
        updateEventDetails(response.data.event); // Actualiza el estado del evento en el contexto
        // También actualizar el estado local de tickets con los datos actualizados
        if (response.data.event.tickets) {
          setTickets(response.data.event.tickets);
        }
      } else {
        toast.error(response.data.message || 'Error al actualizar la capacidad del evento.');
      }
    } catch (error) {
      console.error('Error updating event capacity:', error);
      toast.error('Error al actualizar la capacidad del evento.');
    }
  };

  // Recalcular la capacidad total cada vez que los tickets cambian
  useEffect(() => {
    if (tickets.length > 0) {
      updateEventCapacity(tickets);
    }
  }, [tickets, updateEventCapacity]);


  // Antes de enviar la petición, verificamos si el ticket es de pago y si el usuario tiene cuenta Stripe válida
  const getFreshStripeStatus = async () => {
    const userId = user?._id || user?.id;
    if (!userId) return stripeStatus;
    return refreshStripeStatus(userId);
  };

  // Agregar un nuevo ticket
  const handleAddTicket = async (ticketData) => {
    const { type, price, approvalRequired, name, capacity } = ticketData;

    // Si se intenta crear un ticket de pago sin cuenta Stripe válida, se cancela
    if (type === TICKET_PAID) {
      const currentStripeStatus = await getFreshStripeStatus();
      if (!currentStripeStatus?.hasStripeAccount || !currentStripeStatus?.chargesEnabled) {
        toast.error('No puedes agregar tickets de pago sin tener una cuenta de Stripe validada y con cobros activados. Por favor, activa los pagos en tu configuración.');
        return;
      }
    }

    try {
      const response = await axiosClient.post(`/api/tickets/event/${id}`, {
        type,
        price: type === TICKET_PAID ? Number(price) : 0,
        approvalRequired,
        capacity: Number(capacity),
        name
      });

      if (response.data.success) {
        setTickets(prevTickets => [...prevTickets, response.data.ticket]);
        toast.success('Ticket agregado exitosamente.');
        closeAddModal();
        await updateEventCapacityFromAPI(); // Actualizar la capacidad total del evento
      } else {
        toast.error(response.data.message || 'Error al agregar el ticket.');
      }
    } catch (error) {
      console.error('Error adding ticket:', error);
      toast.error('Error al agregar el ticket.');
    }
  };

  // Actualizar un ticket existente
  const handleUpdateTicket = async (ticketId, updatedFields) => {
    // Si se intenta actualizar un ticket a tipo "paid" sin cuenta Stripe válida, se cancela
    if (updatedFields.type === TICKET_PAID) {
      const currentStripeStatus = await getFreshStripeStatus();
      if (!currentStripeStatus?.hasStripeAccount || !currentStripeStatus?.chargesEnabled) {
        toast.error('No puedes actualizar a un ticket de pago sin tener una cuenta de Stripe validada y con cobros activados.');
        return;
      }
    }

    try {
      const response = await axiosClient.put(`/api/tickets/${ticketId}`, updatedFields);
      if (response.data.success) {
        setTickets(prevTickets =>
          prevTickets.map(ticket => (ticket._id === ticketId ? response.data.ticket : ticket))
        );
        toast.success('Ticket actualizado exitosamente.');
        closeEditModal();
        await updateEventCapacityFromAPI(); // Actualizar la capacidad total del evento
      } else {
        toast.error(response.data.message || 'Error al actualizar el ticket.');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error al actualizar el ticket.');
      }
      console.error('Error updating ticket:', error);
    }
  };

  // Eliminar un ticket
  const handleDeleteTicket = async (ticketId) => {
    if (isEventPast(eventDetails.endDate) || eventDetails?.status === 'cancelled') {
      if (eventDetails?.status === 'cancelled') {
        toast.error('No puedes eliminar tickets de un evento cancelado.');
      } else {
        toast.error('No puedes eliminar tickets de un evento que ya ha finalizado.');
      }
      return;
    }

    try {
      const response = await axiosClient.delete(`/api/tickets/${ticketId}`);
      if (response.data.success) {
        setTickets(prevTickets => prevTickets.filter(ticket => ticket._id !== ticketId));
        toast.success('Ticket eliminado exitosamente.');
        await updateEventCapacityFromAPI(); // Actualizar la capacidad total del evento
      } else {
        toast.error(response.data.message || 'Error al eliminar el ticket.');
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Error al eliminar el ticket.');
    }
  };

  // Manejar el toggle de aprobación requerida
  const handleToggleApproval = async (ticketId, approvalRequired) => {
    try {
      const response = await axiosClient.put(`/api/tickets/${ticketId}`, {
        approvalRequired
      });
      if (response.data.success) {
        setTickets(prevTickets =>
          prevTickets.map(ticket =>
            ticket._id === ticketId
              ? { ...ticket, approvalRequired }
              : ticket
          )
        );
        toast.success(`Aprobación ${approvalRequired ? 'activada' : 'desactivada'} exitosamente.`);
      } else {
        toast.error(response.data.message || 'Error al actualizar la configuración de aprobación.');
      }
    } catch (error) {
      console.error('Error updating approval setting:', error);
      toast.error('Error al actualizar la configuración de aprobación.');
    }
  };

  if (loading || !eventDetails) return (
    <RegistrationContainer>
      <Container>
        <LoadingMsg>
          <Typography $variant="body-1-semibold" color={theme.colors.defaultStrong}>
            {t('loadingTickets')}
          </Typography>
        </LoadingMsg>
      </Container>
    </RegistrationContainer>
  );

  return (
    <RegistrationContainer>
      <Container style={{ paddingBottom: '40px' }}>
        <TicketsHeader>
          <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as='h2'>{t('tickets')}</Typography>

          {/* <Typography $variant="title-4-semibold">{t('tickets')}</Typography> */}
          <Button
            $variant="outline"
            size="l"
            sizeTablet="l"
            sizeMobile="m"
            onClick={openAddModal}
            disabled={isEventPast(eventDetails.endDate) || eventDetails?.status === 'cancelled'}>
            <AddIcon fill={theme.colors.defaultStrong} width={20} height={20} />
            {t('addTicket')}
          </Button>
        </TicketsHeader>

        {/* Lista de Tickets */}
        <TicketsGrid>
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket._id}
              ticket={ticket}
              openEditModal={openEditModal}
              handleDeleteTicket={handleDeleteTicket}
              onToggleApproval={handleToggleApproval}
              isEventCancelled={eventDetails?.status === 'cancelled'}
              isEventPast={isEventPast(eventDetails.endDate)}
            />
          ))}
        </TicketsGrid>

        {/* Modal para Agregar Ticket */}
        {isAddModalOpen && (
          <Modal onClose={closeAddModal} title={t('addTicket')}>
            <AddTicketForm onSubmit={handleAddTicket} onClose={closeAddModal} />
          </Modal>
        )}

        {/* Modal para Editar Ticket */}
        {isEditModalOpen && ticketToEdit && (
          <Modal onClose={closeEditModal} title="Editar Entrada">
            <EditTicketForm
              ticket={ticketToEdit}
              onSubmit={handleUpdateTicket}
              onClose={closeEditModal}
            />
          </Modal>
        )}
      </Container>
      <Container style={{ paddingTop: '0px', paddingBottom: '0px' }}>
        <EmailsHeader>
          <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as='h2'>{t('registrationEmails')}</Typography>
          {/* <Typography $variant="title-4-semibold">{t('registrationEmails')}</Typography> */}
          <Typography $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('customizeEmailsDescription')}</Typography>
          <Tag $variant='brandSubtle'>{t('comingSoon')}</Tag>
        </EmailsHeader>
      </Container>
      <Container style={{ paddingTop: '0px', paddingBottom: '0px' }}>
        <QuestionsHeader>
          <Typography $variant="title-3-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold" as='h2'>{t('additionalQuestions')}</Typography>

          {/* <Typography $variant="title-4-semibold">{t('additionalQuestions')}</Typography> */}
          <Typography $variant="body-2-medium" color={theme.colors.defaultWeak}>{t('additionalQuestionsDescription')}</Typography>
          <Tag $variant='brandSubtle'>{t('comingSoon')}</Tag>
        </QuestionsHeader>
      </Container>
    </RegistrationContainer>
  );
};

export default ManageEventRegistration;


// Estilos
const RegistrationContainer = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-top: ${({ theme }) => theme.sizing.sm};
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  width: 100%;
  padding: 0px ${({ theme }) => theme.sizing.md};

  // padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0px ${({ theme }) => theme.sizing.sm};
  }
`;

const TicketsHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.sizing.md};
  width: 100%;
`;

const TicketsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.sizing.sm};
  }
`;

const EmailsHeader = styled.div`
  border-top: 1px solid ${theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  padding-bottom: 40px;
  padding-top: 40px;
  width: 100%;
`;

const QuestionsHeader = styled.div`
  border-bottom: 1px solid ${theme.border.defaultSubtle};
  border-top: 1px solid ${theme.border.defaultSubtle};
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: flex-start;
  padding-bottom: 40px;
  padding-top: 40px;
  width: 100%;
`;

const LoadingMsg = styled.div`
  width: 100%;
  padding-top: ${({ theme }) => theme.sizing.lg};
  padding-bottom: ${({ theme }) => theme.sizing.lg};
  border-radius: 8px;
  border: 1px solid ${theme.border.defaultSubtle};
  display: flex;
  justify-content: center;
  align-items: center;
`;
